// Debounce utility function
function debounce(func, wait, immediate) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      timeout = null;
      if (!immediate) func(...args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func(...args);
  };
}

if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.input = this.querySelector('.js-search-input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.resultsWrapper = this.querySelector('.predictive-search__results-wrapper');
      this.loadingState = this.querySelector('.predictive-search__loading-state');
      this.resultsContainer = this.querySelector('.predictive-search__results');
      this.loadingText = this.getAttribute('data-loading-text');
      this.addListeners();
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      this.close();
    }

    addListeners() {
      if (this.input) {
        this.input.addEventListener('focus', this.handleFocus.bind(this));
        // Reduced debounce to 150ms for more responsive live search
        this.input.addEventListener('input', debounce(this.handleInput.bind(this), 150));
      }
      
      const searchForm = this.querySelector('.search');
      if (searchForm) {
        searchForm.addEventListener('submit', this.handleSubmit.bind(this));
      }
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      return this.input ? this.input.value.trim() : '';
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      if (
        !this.getQuery().length
        || this.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')
      ) {
        evt.preventDefault();
      }
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        // Show suggested products when input becomes empty
        this.showEmptyState();
        return;
      }

      // Show immediate feedback that search is happening
      this.showSearchingState();
      this.getResults(searchTerm);
    }

    /**
     * Shows immediate searching state for better UX
     */
    showSearchingState() {
      if (!this.hasAttribute('open')) {
        this.open();
        this.showLoading();
      }
    }

    /**
     * Handles 'focus' events on the search field.
     */
    handleFocus() {
      const searchTerm = this.getQuery();
      
      // Always show the search overlay on focus for better UX
      if (searchTerm.length > 0) {
        if (this.getAttribute('results') === 'true') {
          this.open();
        } else {
          this.getResults(searchTerm);
        }
      } else {
        // Show empty state with helpful suggestions
        this.showEmptyState();
      }
    }

    /**
     * Shows an empty state with search suggestions using Shopify's Predictive Search API
     */
    async showEmptyState() {
      this.open();
      
      const container = this.resultsContainer || this.results;
      
      // Show loading state first
      container.innerHTML = `
        <div class="predictive-search__empty">
          <h3 style="margin-bottom: 16px; color: #000000; font-size: 16px;">Suggested for you</h3>
          <div style="text-align: center; padding: 20px;">
            <div class="loading-spinner" style="display: inline-block; width: 20px; height: 20px; border: 2px solid #f3f4f6; border-top: 2px solid #374151; border-radius: 50%; animation: spin 1s linear infinite;"></div>
          </div>
        </div>
      `;

      try {
        // Fetch suggested products using popular search terms
        const popularTerms = ['gloves', 'helmet', 'jersey', 'protection', 'gear'];
        const randomTerm = popularTerms[Math.floor(Math.random() * popularTerms.length)];
        
        const response = await fetch(`${window.Shopify.routes.root}search/suggest.json?q=${randomTerm}&resources[type]=product&resources[limit]=6&resources[limit_scope]=all&resources[options][unavailable_products]=hide`);
        
        if (response.ok) {
          const data = await response.json();
          const products = data.resources.results.products || [];
          
          if (products.length > 0) {
            this.renderSuggestedProducts(products);
          } else {
            this.renderFallbackSuggestions();
          }
        } else {
          this.renderFallbackSuggestions();
        }
      } catch (error) {
        console.warn('Failed to fetch suggested products:', error);
        this.renderFallbackSuggestions();
      }
    }

    /**
     * Formats price correctly - handles both cents and decimal formats
     */
    formatPrice(price) {
      if (!price) return '0.00';
      
      // If price is a string that looks like a decimal (contains a dot)
      if (typeof price === 'string' && price.includes('.')) {
        return parseFloat(price).toFixed(2);
      }
      
      // If price appears to be in cents (large number)
      if (price > 100) {
        return (price / 100).toFixed(2);
      }
      
      // If price is already a small decimal
      return parseFloat(price).toFixed(2);
    }

    /**
     * Renders suggested products from the API
     */
    renderSuggestedProducts(products) {
      const container = this.resultsContainer || this.results;
      
      const productsHtml = products.map(product => `
        <a href="${product.url}" class="suggested-product-link" style="display: flex; align-items: center; padding: 14px 18px; text-decoration: none; color: inherit; border-radius: 10px; margin: 6px 0; transition: all 0.2s ease; border: 1px solid transparent;">
          <div class="suggested-product__media" style="flex-shrink: 0; margin-right: 14px;">
            ${product.featured_image ? `
              <img src="${product.featured_image.url}" alt="${product.featured_image.alt || product.title}" 
                   style="width: 52px; height: 52px; object-fit: cover; border-radius: 8px; border: 1px solid #e5e7eb;">
            ` : `
              <div style="width: 52px; height: 52px; background: #f3f4f6; border-radius: 8px; border: 1px solid #e5e7eb; display: flex; align-items: center; justify-content: center; color: #9ca3af; font-size: 12px;">No image</div>
            `}
          </div>
          <div class="suggested-product__content" style="flex: 1; min-width: 0;">
            <h4 style="margin: 0; font-size: 15px; font-weight: 500; color: #111827; line-height: 1.3; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${product.title}</h4>
            ${product.vendor ? `<p style="margin: 3px 0; font-size: 12px; color: #6b7280; font-weight: 400;">${product.vendor}</p>` : ''}
            <p style="margin: 4px 0 0; font-size: 14px; font-weight: 600; color: #059669;">£${this.formatPrice(product.price_min || product.price)}</p>
          </div>
        </a>
      `).join('');

      container.innerHTML = `
        <div class="predictive-search__empty">
          <h3 style="margin-bottom: 16px; color: #000000; font-size: 16px; text-align: left; padding-left: 4px;">Suggested for you</h3>
          <div class="suggested-products" style="max-height: 300px; overflow-y: auto; text-align: left;">
            ${productsHtml}
          </div>
        </div>
      `;

      // Note: Hover effects are now handled by CSS only to avoid double effects
    }

    /**
     * Renders fallback search term suggestions if API fails
     */
    renderFallbackSuggestions() {
      const container = this.resultsContainer || this.results;
      container.innerHTML = `
        <div class="predictive-search__empty">
          <h3 style="margin-bottom: 16px; color: #374151; font-size: 16px;">Popular searches</h3>
          <div class="search-suggestion-tags" style="display: flex; flex-wrap: wrap; gap: 8px; justify-content: center;">
            <span class="search-tag" style="padding: 8px 16px; background: #f3f4f6; border-radius: 20px; cursor: pointer; font-size: 14px; color: #374151; transition: all 0.2s;">Fox Racing</span>
            <span class="search-tag" style="padding: 8px 16px; background: #f3f4f6; border-radius: 20px; cursor: pointer; font-size: 14px; color: #374151; transition: all 0.2s;">Gloves</span>
            <span class="search-tag" style="padding: 8px 16px; background: #f3f4f6; border-radius: 20px; cursor: pointer; font-size: 14px; color: #374151; transition: all 0.2s;">Helmets</span>
            <span class="search-tag" style="padding: 8px 16px; background: #f3f4f6; border-radius: 20px; cursor: pointer; font-size: 14px; color: #374151; transition: all 0.2s;">Jerseys</span>
            <span class="search-tag" style="padding: 8px 16px; background: #f3f4f6; border-radius: 20px; cursor: pointer; font-size: 14px; color: #374151; transition: all 0.2s;">Protective Gear</span>
          </div>
        </div>
      `;
      
      // Add click handlers for suggestion tags
      this.querySelectorAll('.search-tag').forEach(tag => {
        tag.addEventListener('click', () => {
          this.input.value = tag.textContent;
          this.handleInput();
        });
        
        // Add hover effects
        tag.addEventListener('mouseenter', () => {
          tag.style.background = '#e5e7eb';
          tag.style.transform = 'translateY(-1px)';
        });
        tag.addEventListener('mouseleave', () => {
          tag.style.background = '#f3f4f6';
          tag.style.transform = 'translateY(0)';
        });
      });
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.matches('.tablist__tab')) return;

      // If search field is empty after key press, show suggested products.
      if (!this.getQuery().length && this.hasAttribute('open')) {
        this.showEmptyState();
      }

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (this.hasAttribute('open')) {
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          this.selectOption();
          break;

        case 'Escape':
          this.close();
          break;

        // no default
      }
    }

    /**
     * Initializes the tabbed interface for search results
     */
    initializeTabs() {
      const tabButtons = this.querySelectorAll('.tabs-nav__item');
      const tabPanels = this.querySelectorAll('.predictive-search__results-categories-item');

      tabButtons.forEach((button) => {
        button.addEventListener('click', () => {
          // Remove active state from all tabs and panels
          tabButtons.forEach(btn => btn.setAttribute('aria-expanded', 'false'));
          tabPanels.forEach(panel => panel.setAttribute('hidden', ''));

          // Add active state to clicked tab and corresponding panel
          button.setAttribute('aria-expanded', 'true');
          const targetPanel = this.querySelector(`#${button.getAttribute('aria-controls')}`);
          if (targetPanel) {
            targetPanel.removeAttribute('hidden');
          }
        });
      });
    }

    /**
     * Enhanced keyboard navigation for tabbed results
     */
    handleResultsNav(key) {
      const activePanel = this.querySelector('.predictive-search__results-categories-item:not([hidden])');
      
      if (!activePanel) return;

      const selectedResult = activePanel.querySelector('[aria-selected="true"]');
      const allResults = activePanel.querySelectorAll('.js-search-link');
      let resultToSelect = allResults[0];

      if (key === 'ArrowUp') {
        if (!selectedResult) return;

        // Find the parent item and get previous sibling
        const currentItem = selectedResult.closest('li');
        const prevItem = currentItem?.previousElementSibling;
        resultToSelect = prevItem?.querySelector('.js-search-link') || allResults[allResults.length - 1];
      } else if (key === 'ArrowDown') {
        if (selectedResult) {
          const currentItem = selectedResult.closest('li');
          const nextItem = currentItem?.nextElementSibling;
          resultToSelect = nextItem?.querySelector('.js-search-link') || allResults[0];
        }
      }

      this.statusEl.textContent = '';

      if (resultToSelect && resultToSelect !== selectedResult) {
        // Clear previous selection
        if (selectedResult) {
          selectedResult.closest('li')?.removeAttribute('aria-selected');
        }

        // Set new selection
        resultToSelect.closest('li')?.setAttribute('aria-selected', 'true');
        this.setLiveRegionText(resultToSelect.textContent);
        this.input.setAttribute('aria-activedescendant', resultToSelect.closest('li')?.id || '');
      }
    }

    /**
     * Enhanced selectOption method for tabbed interface
     */
    selectOption() {
      const activePanel = this.querySelector('.predictive-search__results-categories-item:not([hidden])');
      const selectedResult = activePanel?.querySelector('[aria-selected="true"] .js-search-link');
      if (selectedResult) selectedResult.click();
    }

    /**
     * Fetches the search results from the predictive search API.
     * @param {string} searchTerm - Search term.
     */
    getResults(searchTerm) {
      const queryKey = searchTerm.replace(' ', '-').toLowerCase();
      this.setLiveRegionLoadingState();
      
      // Show loading state
      this.showLoading();

      if (this.cachedResults[queryKey]) {
        this.renderResults(this.cachedResults[queryKey]);
        return;
      }

      // Build search parameters including SKU search if enabled
      let searchParams = `q=${encodeURIComponent(searchTerm)}`;
      searchParams += `&${encodeURIComponent('resources[type]')}=product,collection,page,article`;
      searchParams += `&${encodeURIComponent('resources[limit]')}=10`;
      searchParams += `&section_id=predictive-search-results`;
      
      // Add SKU search if enabled in theme settings
      if (window.theme && window.theme.settings && window.theme.settings.pSearchIncludeSkus) {
        searchParams += `&${encodeURIComponent('resources[options][fields]')}=title,product_type,vendor,variants.sku`;
      }

      fetch(
        `${window.theme.routes.predictiveSearch}?${searchParams}`
      )
        .then((response) => {
          if (!response.ok) {
            var error = new Error(response.status);
            this.close();
            throw error;
          }
          return response.text();
        })
        .then((text) => {
          const resultsMarkup = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#shopify-section-predictive-search-results').innerHTML;
          
          // Cache the results
          this.cachedResults[queryKey] = resultsMarkup;
          this.renderResults(resultsMarkup);
        })
        .catch((error) => {
          this.close();
          throw error;
        });
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;

      setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup) {
      // Hide loading state
      if (this.loadingState) {
        this.loadingState.hidden = true;
      }
      
      // Show and populate results
      if (this.resultsContainer) {
        this.resultsContainer.innerHTML = resultsMarkup;
        this.resultsContainer.hidden = false;
      } else {
        this.results.innerHTML = resultsMarkup;
      }
      
      this.setAttribute('results', true);
      this.initializeTabs();
      this.open();
    }

    showLoading() {
      if (!this.hasAttribute('open')) {
        this.open();
      }
      
      const container = this.resultsContainer || this.results;
      container.innerHTML = `
        <div class="predictive-search__loading">
          <div style="width: 24px; height: 24px; border: 2px solid #f3f4f6; border-top: 2px solid #3b82f6; border-radius: 50%; animation: spin 1s linear infinite; margin: 0 auto 16px;"></div>
          <p style="margin: 0; font-size: 14px; color: #6b7280;">Searching...</p>
        </div>
      `;
      
      if (this.loadingState) {
        this.loadingState.hidden = false;
      }
      if (this.resultsContainer) {
        this.resultsContainer.hidden = false;
      }
    }

    /**
     * Opens the results dropdown.
     */
    open() {
      if (this.hasAttribute('open')) return;
      
      this.setAttribute('open', '');
      this.input.setAttribute('aria-expanded', 'true');
      this.resetBtn.hidden = false;
      
      if (this.overlay) {
        this.overlay.classList.add('is-visible');
      }
      
      if (this.resultsWrapper) {
        this.resultsWrapper.style.display = 'block';
      }
      
      // Make sure results container is visible
      if (this.resultsContainer) {
        this.resultsContainer.hidden = false;
      }
      
      // Make sure loading state is hidden when opening
      if (this.loadingState) {
        this.loadingState.hidden = true;
      }
      
      document.body.classList.add('overlay-predictive-search');

      // Bind event handlers
      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);

      // Add event listeners
      this.addEventListener('keydown', this.keydownHandler);
      if (this.resetBtn) {
        this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      }
      if (this.overlay) {
        this.overlay.addEventListener('click', this.overlayClickHandler);
      }
    }

    /**
     * Closes the results dropdown.
     * @param {object} evt - Event object.
     */
    close(evt) {
      if (!this.hasAttribute('open')) return;
      
      // If reset button was clicked, clear the input
      if (evt && evt.target === this.resetBtn) {
        this.input.value = '';
        this.removeAttribute('results');
        this.input.focus();
      }

      // Clear any selected results
      const selected = this.querySelector('[aria-selected="true"]');
      if (selected) {
        selected.setAttribute('aria-selected', 'false');
      }

      // Reset UI state
      this.removeAttribute('open');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.setAttribute('aria-activedescendant', '');
      this.resetBtn.hidden = true;
      
      if (this.overlay) {
        this.overlay.classList.remove('is-visible');
      }
      
      if (this.resultsWrapper) {
        this.resultsWrapper.style.display = 'none';
      }
      
      if (this.loadingState) {
        this.loadingState.hidden = true;
      }
      
      if (this.resultsContainer) {
        this.resultsContainer.hidden = true;
      }
      
      document.body.classList.remove('overlay-predictive-search');

      // Remove event listeners
      this.removeEventListener('keydown', this.keydownHandler);
      if (this.resetBtn) {
        this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
      }
      if (this.overlay) {
        this.overlay.removeEventListener('click', this.overlayClickHandler);
      }
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
